import type { FilterConfig, FilterState } from '@/components/filter-bar';
import { CLIENT_TYPE_LABELS, DEFAULT_OPEN_STATUSES, STATUS_LABELS, TYPOLOGY_LABELS } from '@/features/market-surveillance/surveillance-meta';
import {
  SURVEILLANCE_CLIENT_TYPES,
  SURVEILLANCE_STATUSES,
  type SurveillanceAlertFilters,
  type SurveillanceClientType,
  type SurveillanceStatus,
} from '@/services/api/market-surveillance';
import { MS_PER_DAY, utcEndOfDayIso, utcStartOfDayIso } from '@/utils/date';

// TableFilters options are value-only strings, so the human labels ARE the option
// values shown in chips/dialog; filtersToRequest reverse-maps them to wire codes.
const STATUS_BY_LABEL = Object.fromEntries(SURVEILLANCE_STATUSES.map((status) => [STATUS_LABELS[status], status])) as Record<string, SurveillanceStatus>;
const CLIENT_TYPE_BY_LABEL = Object.fromEntries(SURVEILLANCE_CLIENT_TYPES.map((clientType) => [CLIENT_TYPE_LABELS[clientType], clientType])) as Record<
  string,
  SurveillanceClientType
>;
const TYPOLOGY_BY_LABEL: Record<string, string> = Object.fromEntries(Object.entries(TYPOLOGY_LABELS).map(([code, label]) => [label, code]));

// Server-side matching (bop-api applies the predicate) — same idiom as transaction-filters:
// getValue is never invoked and the substring claim is unverified, so helper copy is suppressed.
const TEXT_HELPER_TEXT = '';

export const ALERT_FILTER_CONFIGS: Array<FilterConfig<unknown>> = [
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'alertId', matchMode: 'substring', title: 'Alert ID', type: 'multi-text' },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'pid', matchMode: 'substring', title: 'PID', type: 'multi-text' },
  { getValue: () => undefined, key: 'typology', options: Object.values(TYPOLOGY_LABELS).sort(), title: 'Type', type: 'multi-choice' },
  {
    getValue: () => undefined,
    key: 'clientType',
    options: SURVEILLANCE_CLIENT_TYPES.map((clientType) => CLIENT_TYPE_LABELS[clientType]),
    title: 'Client Type',
    type: 'multi-choice',
  },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'instrument', matchMode: 'substring', title: 'Instrument', type: 'multi-text' },
  // Lifecycle order on purpose (not sorted) — mirrors the status state machine
  { getValue: () => undefined, key: 'status', options: SURVEILLANCE_STATUSES.map((status) => STATUS_LABELS[status]), title: 'Status', type: 'multi-choice' },
  { getValue: () => undefined, key: 'riskScore', title: 'Risk Score', type: 'range' },
  { getValue: () => undefined, key: 'createdAt', title: 'Created At', type: 'date' },
];

/** The analyst queue default — a visible, clearable Status chip; clearing it IS the archive view */
export const INITIAL_ALERT_FILTER_STATE: FilterState = { status: DEFAULT_OPEN_STATUSES.map((status) => STATUS_LABELS[status]) };

/** 'custom' bounds come from the createdAt dates, not this token. No unbounded option on
 * purpose — every alerts query carries a created_at window so QuestDB never full-scans. */
export type AlertTimeRange = '1d' | '7d' | '30d' | 'custom';

export const ALERT_TIME_RANGE_OPTIONS: Array<{ value: Exclude<AlertTimeRange, 'custom'>; label: string }> = [
  { value: '1d', label: 'Last day' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last month' },
];

export const DEFAULT_ALERT_TIME_RANGE: AlertTimeRange = '1d';

const TIME_RANGE_DAYS: Record<Exclude<AlertTimeRange, 'custom'>, number> = { '1d': 1, '7d': 7, '30d': 30 };

export const timeRangeToRequest = (range: AlertTimeRange, now: Date = new Date()): Pick<SurveillanceAlertFilters, 'from'> =>
  range === 'custom' ? {} : { from: new Date(now.getTime() - TIME_RANGE_DAYS[range] * MS_PER_DAY).toISOString() };

const parseBound = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const filtersToRequest = (state: FilterState): SurveillanceAlertFilters => {
  const statuses = (state.status ?? []).map((label) => STATUS_BY_LABEL[label]).filter((status): status is SurveillanceStatus => status !== undefined);
  const typologies = (state.typology ?? []).map((label) => TYPOLOGY_BY_LABEL[label] ?? label);
  const clientTypes = (state.clientType ?? []).map((label) => CLIENT_TYPE_BY_LABEL[label]).filter((clientType): clientType is SurveillanceClientType => clientType !== undefined);
  // Instrument codes are uppercase symbols server-side (ETH_USD_P) — normalise analyst input
  const instruments = (state.instrument ?? []).map((value) => value.trim().toUpperCase()).filter(Boolean);
  const alertId = state.alertId?.[0];
  const pid = state.pid?.[0];
  const riskScoreMin = parseBound(state.riskScore?.[0]);
  const riskScoreMax = parseBound(state.riskScore?.[1]);
  const createdFrom = state.createdAt?.[0];
  const createdTo = state.createdAt?.[1];

  return {
    ...(alertId ? { alertId } : {}),
    ...(pid ? { pid } : {}),
    ...(typologies.length ? { typology: typologies } : {}),
    ...(statuses.length ? { status: statuses } : {}),
    ...(clientTypes.length ? { clientType: clientTypes } : {}),
    ...(instruments.length ? { instrument: instruments } : {}),
    ...(riskScoreMin !== undefined ? { riskScoreMin } : {}),
    ...(riskScoreMax !== undefined ? { riskScoreMax } : {}),
    ...(createdFrom ? { from: utcStartOfDayIso(createdFrom) } : {}),
    ...(createdTo ? { to: utcEndOfDayIso(createdTo) } : {}),
  };
};
